import {
  commandRequest,
  commandLabel,
  type UiCommandRequest,
} from '../../../agents/context/ui-command-registry.js';
import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import {
  Bubble,
  Sender,
  Sources,
  XProvider,
  type BubbleItemType,
  type SourcesProps,
} from '@ant-design/x';
import type {
  ChatContextRef,
  Message,
  StructuredMessagePayload,
  ToolCall,
} from '@govard/ai-chat-kit';
import { Button, Input, message, Tooltip } from 'antd';
import {
  Check,
  Sparkles,
  Copy,
  Pin,
  ListTodo,
  User,
  Bot,
  Mic,
  MicOff,
  Square,
  RotateCcw,
  Paperclip,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Bookmark,
  Pencil,
  Brain,
  Command,
  LayoutDashboard,
  Loader2,
  X,
} from 'lucide-react';
import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ComponentRef,
  type ReactElement,
} from 'react';

import { BoundedMarkdownRenderer } from './BoundedMarkdownRenderer.js';
import {
  StructuredMessageRenderer,
  parseApprovalCardEntry,
} from './snippets/StructuredMessageRenderer.js';
import { TaskKanbanChatSnippet } from './snippets/TaskKanbanChatSnippet.js';
import type { ArtifactContent } from './ArtifactCanvas.js';
import { localizedToolName } from '../lib/aiUiLabels.js';
import { useToolCallDebug } from '../lib/toolCallDebug.js';
import {
  resolveSpeechRecognition,
  startRecognition,
  type SpeechRecognitionLike,
} from '../lib/speechRecognition.js';
import { ChatWelcomeHero } from './ChatWelcomeHero.js';
import { SlashCommandMenu } from './SlashCommandMenu.js';
import { MentionMenu } from './MentionMenu.js';
import { LobeEntityFeed } from './snippets/LobeEntityFeed.js';
import {
  EntityChipPicker,
  DEFAULT_ENTITY_CHIP_MAX,
  type ChatEntityChip,
  type MentionOption,
} from './EntityChipPicker.js';
import type { EntityChatAgent, ChatUiMessage } from '../hooks/useEntityChat.js';
import type { ApprovalItem } from '../../approvals/api/approvals.client.js';
import type { CrmActionPlanCallbacks } from './CrmActionPlanCard.js';
import { useCreateMemoryMutation } from '../../memory/hooks/useMemoryMutations.js';
import {
  createCommandContextRef,
  type UiCommandKey,
} from '../../../agents/context/result-action-commands.js';
import { uploadAttachment } from '../../attachments/api/attachments.client.js';
import { localizedFileInputError } from '../lib/fileInputErrors.js';

type TextAreaProps = ComponentProps<typeof Input.TextArea>;
type TextAreaRef = ComponentRef<typeof Input.TextArea>;
type SourcesItem = NonNullable<SourcesProps['items']>[number];
type EmailDraft = Extract<StructuredMessagePayload, { type: 'email_draft' }>['draft'];
type DealActionPlan = Extract<StructuredMessagePayload, { type: 'deal_action_plan' }>['plan'];
type MeetingOutcomeMeeting = Extract<
  StructuredMessagePayload,
  { type: 'meeting_outcome' }
>['meeting'];
interface MeetingTaskCandidate {
  title: string;
  rationale?: string | null;
}

const DRAFT_STORAGE_KEY_PREFIX = 'govard.crm.ai-chat.draft.v2';

export interface ComposerDraftScope {
  workspaceId: string;
  userId: string;
}

interface StoredComposerDraft {
  text: string;
  contextRefs: ChatEntityChip[];
}

/**
 * PR 5: a file uploaded with the conversation owner that rides along the next
 * outgoing message (`attachmentId` on the chat request). Detached after the
 * message is sent or restored together with the draft on a failed send.
 */
interface PendingFileAttachment {
  id: string;
  fileName: string;
}

const EMPTY_STORED_DRAFT: StoredComposerDraft = { text: '', contextRefs: [] };
const CHAT_ENTITY_CHIP_TYPES = new Set<ChatEntityChip['type']>([
  'deal',
  'contact',
  'company',
  'task',
  'note',
  'user',
  'attachment',
]);

function getDraftStorageKey(scope: ComposerDraftScope, conversationId: string): string {
  const identity = [scope.workspaceId, scope.userId, conversationId]
    .map((value) => encodeURIComponent(value))
    .join(':');
  return `${DRAFT_STORAGE_KEY_PREFIX}:${identity}`;
}

function isStoredEntityChip(value: unknown): value is ChatEntityChip {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const chip = value as Record<string, unknown>;
  return (
    typeof chip.type === 'string' &&
    CHAT_ENTITY_CHIP_TYPES.has(chip.type as ChatEntityChip['type']) &&
    typeof chip.id === 'string' &&
    chip.id.trim() !== '' &&
    typeof chip.label === 'string'
  );
}

function readStoredDraft(scope: ComposerDraftScope, conversationId: string): StoredComposerDraft {
  try {
    const raw = sessionStorage.getItem(getDraftStorageKey(scope, conversationId));
    if (raw === null) {
      return EMPTY_STORED_DRAFT;
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return EMPTY_STORED_DRAFT;
    }
    const storedDraft = parsed as { text: unknown; contextRefs: unknown };
    if (typeof storedDraft.text !== 'string' || !Array.isArray(storedDraft.contextRefs)) {
      return EMPTY_STORED_DRAFT;
    }
    const contextRefs = storedDraft.contextRefs;
    if (contextRefs.length > DEFAULT_ENTITY_CHIP_MAX || !contextRefs.every(isStoredEntityChip)) {
      return EMPTY_STORED_DRAFT;
    }
    const deduplicatedRefs = new Map<string, ChatEntityChip>();
    for (const ref of contextRefs) {
      deduplicatedRefs.set(`${ref.type}:${ref.id}`, ref);
    }
    if (deduplicatedRefs.size !== contextRefs.length) {
      return EMPTY_STORED_DRAFT;
    }
    return { text: storedDraft.text, contextRefs: [...deduplicatedRefs.values()] };
  } catch {
    return EMPTY_STORED_DRAFT;
  }
}

function writeStoredDraft(
  scope: ComposerDraftScope,
  conversationId: string,
  draft: StoredComposerDraft,
): void {
  try {
    const key = getDraftStorageKey(scope, conversationId);
    if (draft.text.trim().length === 0 && draft.contextRefs.length === 0) {
      sessionStorage.removeItem(key);
      return;
    }
    sessionStorage.setItem(key, JSON.stringify(draft));
  } catch {
    // Storage unavailable — the in-memory composer remains usable.
  }
}

const TestableTextArea = forwardRef<TextAreaRef, TextAreaProps>((props, ref) => (
  <Input.TextArea
    {...props}
    className={`resize-none ${props.className ?? ''}`}
    style={{ ...props.style, resize: 'none' }}
    ref={ref}
    data-testid="chatpanel-input"
  />
));

TestableTextArea.displayName = 'TestableTextArea';

type RetryableChatAgent = EntityChatAgent & {
  cancelStream?: () => void;
  retryLastRequest?: () => Promise<void>;
  canRetryLastRequest?: boolean;
};

export interface AntDesignXChatSurfaceProps extends CrmActionPlanCallbacks {
  agent: RetryableChatAgent;
  placeholder: string;
  submitLabel: string;
  disabled?: boolean;
  conversationId?: string | null;
  /**
   * Creates (or returns the current) conversation for a file picked in a
   * fresh chat that has no server row yet. The parent owns the same creation
   * call it uses for the first message and must switch its own
   * conversationId to the result. Resolves with the conversation id, or
   * null when the conversation could not be created.
   */
  onEnsureConversation?: (() => Promise<string | null>) | undefined;
  /** Storage identity for a local composer draft. Drafts are never shared across scopes. */
  draftScope?: ComposerDraftScope | undefined;
  onSendEmailDraft?: ((draft: EmailDraft) => void) | undefined;
  onPrepareDealFollowUp?: ((dealId: string) => void) | undefined;
  onProposeTouchStrategy?: ((dealId: string) => void) | undefined;
  onCreateDealTasks?:
    | ((plan: DealActionPlan, actions: DealActionPlan['actions']) => void)
    | undefined;
  createMeetingTasksDisabled?: boolean;
  onCreateMeetingTasks?:
    | ((meeting: MeetingOutcomeMeeting, actions: readonly MeetingTaskCandidate[]) => void)
    | undefined;
  prepareMeetingFollowUpDisabled?: boolean;
  onPrepareMeetingFollowUp?: ((meeting: MeetingOutcomeMeeting) => void) | undefined;
  onOpenCanvas?: ((artifact: ArtifactContent) => void) | undefined;
  onOpenEntity?: ((type: 'deal' | 'contact', id: string, title?: string) => void) | undefined;
  onExecutePrompt?: ((prompt: string) => Promise<void>) | undefined;
  onExecuteScenario?:
    | ((
        request:
          | string
          | {
              prompt: string;
              command?: UiCommandRequest;
              attachmentId?: string;
              contextRefs?: ChatContextRef[];
            },
      ) => void)
    | undefined;
  onRequestModelUpgrade?:
    | ((offer: Extract<StructuredMessagePayload, { type: 'model_upgrade_offer' }>) => void)
    | undefined;
  onApprovalDecisionApplied?: ((approval: ApprovalItem) => void) | undefined;
  /** Entity context of the chat — passed into memory metadata when remembering an answer. */
  contextEntityType?: 'deal' | 'contact' | 'project' | undefined;
  contextEntityId?: string | undefined;
  onContextRefsChange?: ((refs: ChatContextRef[]) => void) | undefined;
}

function toBubbleRole(message: Message): string {
  if (message.role === 'user') {
    return 'user';
  }

  if (
    message.role === 'assistant' ||
    message.role === 'tool' ||
    message.structuredPayload !== undefined
  ) {
    return 'ai';
  }

  return 'system';
}

// Assistant/system bubbles must own the whole row: `.ant-bubble-body` is a
// flex item sized by its content by default, so a wide structured card (an
// action plan) collapses to its intrinsic width and container queries inside
// it wrongly switch to the narrow layout. `body: flex + min-width: 0` sizes
// the card against the real chat width; avatars stay fixed and user bubbles
// keep their compact content-sized shape on the right.
export const chatBubbleRoles = {
  ai: {
    placement: 'start',
    variant: 'borderless',
    shape: 'default',
    styles: {
      root: { width: '100%' },
      body: { flex: '1 1 0', minWidth: 0 },
      content: { width: '100%', maxWidth: '100%', padding: '4px 0' },
    },
    avatar: (
      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-100 border border-amber-300/80 text-amber-700 shadow-2xs shrink-0">
        <Sparkles size={14} />
      </div>
    ),
  },
  user: {
    placement: 'end',
    variant: 'filled',
    shape: 'default',
    styles: {
      content: {
        background: 'var(--g-fill)',
        color: 'var(--color-text)',
        borderRadius: '16px 16px 4px 16px',
        padding: '10px 16px',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
      },
    },
    avatar: (
      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-stone-800 border border-stone-700 text-white shadow-2xs shrink-0">
        <User size={14} />
      </div>
    ),
  },
  system: {
    placement: 'start',
    variant: 'outlined',
    shape: 'default',
    styles: {
      root: { width: '100%' },
      body: { flex: '1 1 0', minWidth: 0 },
      content: { width: '100%', maxWidth: '100%', padding: '4px 0' },
    },
    avatar: (
      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-stone-100 border border-stone-200 text-stone-600 shrink-0">
        <Bot size={14} />
      </div>
    ),
  },
} as const;

const TASK_TOOL_NAMES: ReadonlySet<string> = new Set([
  'create_task',
  'update_task',
  'set_task_status',
  'assign_task',
  'delete_task',
  'link_task',
  'unlink_task',
  'set_task_due_date',
]);

/** Deal id from the chat context when the agent worked with tasks (task tools). */
function extractTaskBoardDealId(message: Message): string | undefined {
  for (const call of message.toolCalls ?? []) {
    if (!TASK_TOOL_NAMES.has(call.name)) continue;
    // args may be missing on streamed/historical tool calls — treat as absent.
    const dealId = call.args?.dealId;
    if (typeof dealId === 'string') return dealId;
  }
  const payload = message.structuredPayload;
  if (payload?.type === 'deal') return payload.deal?.id;
  if (payload?.type === 'deal_cockpit') return payload.cockpit?.deal?.id;
  return undefined;
}

function getSources(message: Message): SourcesItem[] {
  const payload = message.structuredPayload;
  if (!payload) {
    return [];
  }

  if (payload.type === 'deal') {
    if (payload.deal === undefined) {
      return [];
    }
    return [
      {
        key: payload.deal.id,
        title: payload.deal.title,
        url: `/deals/${payload.deal.id}`,
      },
    ];
  }

  if (payload.type === 'contact') {
    return [
      {
        key: payload.contact.id,
        title: payload.contact.name,
        url: `/contacts/${payload.contact.id}`,
      },
    ];
  }

  return [];
}

function getToolDetail(toolCall: ToolCall): string | null {
  const args = toolCall.args as Record<string, unknown> | undefined;
  if (!args) return null;

  if (toolCall.name === 'web_fetch' && typeof args['url'] === 'string') {
    try {
      const parsed = new URL(args['url']);
      return parsed.hostname.replace(/^www\./, '');
    } catch {
      return args['url'].length > 25 ? `${args['url'].slice(0, 22)}...` : args['url'];
    }
  }

  if (toolCall.name === 'web_search') {
    const raw =
      typeof args['query'] === 'string'
        ? args['query']
        : typeof args['q'] === 'string'
          ? args['q']
          : '';
    const q = raw.trim();
    if (q) {
      return q.length > 28 ? `“${q.slice(0, 25)}...”` : `“${q}”`;
    }
  }

  if (toolCall.name === 'deep_research' && typeof args['query'] === 'string') {
    const q = args['query'].trim();
    return q.length > 28 ? `“${q.slice(0, 25)}...”` : `“${q}”`;
  }

  if (typeof args['query'] === 'string' && args['query'].trim() !== '') {
    const q = args['query'].trim();
    return q.length > 28 ? `“${q.slice(0, 25)}...”` : `“${q}”`;
  }
  if (typeof args['q'] === 'string' && args['q'].trim() !== '') {
    const q = args['q'].trim();
    return q.length > 28 ? `“${q.slice(0, 25)}...”` : `“${q}”`;
  }

  if (typeof args['title'] === 'string' && args['title'].trim() !== '') {
    const t = args['title'].trim();
    return t.length > 25 ? `“${t.slice(0, 22)}...”` : `“${t}”`;
  }
  if (typeof args['name'] === 'string' && args['name'].trim() !== '') {
    const n = args['name'].trim();
    return n.length > 25 ? `“${n.slice(0, 22)}...”` : `“${n}”`;
  }

  return null;
}

function sanitizeChatProse(content: string): string {
  if (
    !content ||
    (!content.includes('tool') && !content.includes('DSML') && !content.includes('｜'))
  ) {
    return content;
  }
  return content
    .replace(
      /<\s*\|\s*DSML\s*\|\s*tool_calls\s*>[\s\S]*?<\s*\/\s*\|\s*DSML\s*\|\s*tool_calls\s*>/gi,
      '',
    )
    .replace(/<\s*\|\s*DSML\s*\|\s*invoke[\s\S]*?<\s*\/\s*\|\s*DSML\s*\|\s*invoke\s*>/gi, '')
    .replace(/<[|｜]tool calls begin[|｜]>[\s\S]*?<[|｜]tool calls end[|｜]>/gi, '')
    .replace(/<[|｜]tool call begin[|｜]>[\s\S]*?<[|｜]tool call end[|｜]>/gi, '')
    .replace(/<[|｜]tool sep[|｜]>/gi, '')
    .trim();
}

function ToolCallPill({
  toolCall,
  isStreaming,
  status,
}: {
  toolCall: ToolCall;
  isStreaming: boolean;
  status?: 'ok' | 'error' | undefined;
}): ReactElement {
  const { enabled: debugEnabled } = useToolCallDebug();
  const toolTitle = localizedToolName(toolCall.name, i18n.t);
  const detail = getToolDetail(toolCall);
  const resolvedStatus: 'running' | 'ok' | 'error' =
    status !== undefined ? status : isStreaming ? 'running' : 'ok';
  const isError = resolvedStatus === 'error';

  const pill = (
    <div
      key={toolCall.id}
      data-testid={`tool-call-chip-${toolCall.id}`}
      data-status={resolvedStatus}
      className={`my-0.5 inline-flex max-w-[280px] items-center gap-1.5 truncate rounded-md border px-2 py-0.5 text-[11px] ${
        isError
          ? 'border-rose-200/70 bg-rose-50/80 text-rose-700'
          : 'border-zinc-200/60 bg-zinc-50/80 text-zinc-600'
      }`}
    >
      {resolvedStatus === 'running' ? (
        <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-amber-500" />
      ) : isError ? (
        <AlertTriangle size={11} className="shrink-0 text-rose-500" strokeWidth={2} />
      ) : (
        <Check size={11} className="shrink-0 text-emerald-600" strokeWidth={2} />
      )}
      <span
        className={`shrink-0 ${isError ? 'font-medium text-rose-700' : 'font-medium text-zinc-700'}`}
      >
        {toolTitle}
      </span>
      {debugEnabled && (
        <span className="shrink-0 font-mono text-[10px] text-zinc-400">{toolCall.name}</span>
      )}
      {detail && (
        <>
          <span className="text-zinc-400">·</span>
          <span className="truncate font-normal text-zinc-500" title={detail}>
            {detail}
          </span>
        </>
      )}
    </div>
  );

  // The raw payload is an internal diagnostics surface: regular users see the
  // human-readable action and its target, the working team can turn the raw
  // tool name and argument JSON back on with the toolbar debug switch.
  if (!debugEnabled) return pill;

  const hasArgs = toolCall.args && Object.keys(toolCall.args).length > 0;
  if (!hasArgs && !detail) return pill;

  const tooltipTitle = (
    <div className="max-w-xs text-xs">
      <div className="mb-1 font-semibold">{toolCall.name}</div>
      {typeof (toolCall.args as Record<string, unknown>)?.url === 'string' && (
        <div className="mb-1 break-all opacity-[0.85]">
          URL: {String((toolCall.args as Record<string, unknown>).url)}
        </div>
      )}
      {typeof (toolCall.args as Record<string, unknown>)?.query === 'string' && (
        <div className="mb-1 break-all opacity-[0.85]">
          Query: {String((toolCall.args as Record<string, unknown>).query)}
        </div>
      )}
      <pre className="max-h-32 overflow-auto rounded p-1 text-[10px] opacity-[0.85]">
        {JSON.stringify(toolCall.args, null, 2)}
      </pre>
    </div>
  );

  return (
    <Tooltip title={tooltipTitle} placement="top">
      {pill}
    </Tooltip>
  );
}

const AGENT_NAME_KEYS: Record<string, string> = {
  'crm-deal-specialist': 'deal_specialist',
  'crm-tasks-specialist': 'tasks_specialist',
  'crm-views-builder': 'views_builder',
};

function localizedAgentName(agentId: string): string {
  const key = AGENT_NAME_KEYS[agentId] ?? 'chat_assistant';
  return i18n.t(`crm.ai_chat.agent.${key}`);
}

function AgentAttributionBadge({ agentId }: { agentId: string }): ReactElement {
  return (
    <span
      data-testid="chat-agent-attribution"
      className="inline-flex items-center gap-1 text-[11px] text-stone-400"
    >
      <Bot size={11} className="text-amber-600" aria-hidden="true" />
      <span>
        {i18n.t('crm.ai_chat.agent.responding')}{' '}
        <span className="font-medium text-amber-700">{localizedAgentName(agentId)}</span>
      </span>
    </span>
  );
}

function AssistantMessageFooter({
  messageId,
  content,
  onExecuteScenario,
  onRetry,
  disabled = false,
  contextEntityType,
  contextEntityId,
  selectedEntity,
  allowInferredDealNote,
}: {
  messageId: string;
  content: string;
  onExecuteScenario:
    | ((
        request:
          | string
          | {
              prompt: string;
              command?: UiCommandRequest;
              attachmentId?: string;
              contextRefs?: ChatContextRef[];
            },
      ) => void)
    | undefined;
  onRetry?: (() => void) | undefined;
  disabled?: boolean;
  contextEntityType?: 'deal' | 'contact' | 'project' | undefined;
  contextEntityId?: string | undefined;
  selectedEntity?:
    | {
        type: Exclude<ChatContextRef['type'], 'command' | 'assistant_message' | 'user'>;
        id: string;
        label: string;
        inferred: boolean;
      }
    | undefined;
  allowInferredDealNote: boolean;
}): ReactElement {
  const [copied, setCopied] = useState(false);
  const [remembered, setRemembered] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rememberTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (copyTimerRef.current !== null) clearTimeout(copyTimerRef.current);
      if (rememberTimerRef.current !== null) clearTimeout(rememberTimerRef.current);
    };
  }, []);
  const createMemoryMutation = useCreateMemoryMutation();
  const noteDeal =
    contextEntityType === 'deal' && contextEntityId !== undefined
      ? { id: contextEntityId, label: selectedEntity?.label ?? contextEntityId, inferred: false }
      : allowInferredDealNote && selectedEntity?.type === 'deal'
        ? selectedEntity
        : undefined;

  const executeResultAction = (
    command: UiCommandKey,
    _commandLabel: string,
    _prompt: string,
    entityRef?: ChatContextRef,
  ): void => {
    const contextRefs: ChatContextRef[] = [
      createCommandContextRef(command, _commandLabel),
      { type: 'assistant_message', id: messageId, label: i18n.t('crm.ai_chat.actions.copy') },
    ];
    if (entityRef !== undefined) contextRefs.push(entityRef);
    onExecuteScenario?.({
      prompt: commandLabel(commandRequest(command), i18n.locale),
      command: commandRequest(command, {
        sourceMessageId: messageId,
        ...(entityRef?.type === 'deal' ? { dealId: entityRef.id } : {}),
      }),
      contextRefs: contextRefs.filter(
        (ref) => ref.type !== 'command' && ref.type !== 'assistant_message',
      ),
    });
  };

  const handleCopy = (): void => {
    // clipboard.writeText rejects when the page lacks clipboard permission;
    // without the catch the checkmark appeared even though nothing was copied.
    navigator.clipboard
      .writeText(content)
      .then(() => {
        if (!mountedRef.current) return;
        setCopied(true);
        if (copyTimerRef.current !== null) clearTimeout(copyTimerRef.current);
        copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        void message.error(i18n.t('crm.ai_chat.error.copy_failed'));
      });
  };

  const handleRemember = (): void => {
    const memoryMetadata: { dealId?: string; contactId?: string; projectId?: string } =
      contextEntityType === undefined || contextEntityId === undefined
        ? {}
        : contextEntityType === 'deal'
          ? { dealId: contextEntityId }
          : contextEntityType === 'project'
            ? { projectId: contextEntityId }
            : { contactId: contextEntityId };
    void createMemoryMutation
      .mutateAsync({ valueText: content, level: 'l2', metadata: memoryMetadata })
      .then(() => {
        if (!mountedRef.current) return;
        setRemembered(true);
        if (rememberTimerRef.current !== null) clearTimeout(rememberTimerRef.current);
        rememberTimerRef.current = setTimeout(() => setRemembered(false), 2000);
      })
      .catch(() => {
        void message.error(i18n.t('crm.ai_chat.error.remember_failed'));
      });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 pt-1 text-[11px] text-stone-400">
      <button
        type="button"
        onClick={handleCopy}
        className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-stone-400 dark:text-stone-400 hover:bg-stone-200/60 dark:hover:bg-stone-800/80 hover:text-stone-800 transition cursor-pointer"
      >
        {copied ? <Check size={11} className="text-emerald-600" /> : <Copy size={11} />}
        <span>
          {copied ? i18n.t('crm.ai_chat.actions.copied') : i18n.t('crm.ai_chat.actions.copy')}
        </span>
      </button>

      <button
        type="button"
        data-testid={`chat-message-remember-${messageId}`}
        disabled={disabled}
        onClick={handleRemember}
        className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-stone-400 dark:text-stone-400 hover:bg-stone-200/60 dark:hover:bg-stone-800/80 hover:text-amber-800 transition cursor-pointer disabled:opacity-50"
      >
        {remembered ? (
          <Check size={11} className="text-emerald-600" />
        ) : (
          <Bookmark size={11} className="text-amber-600" />
        )}
        <span>
          {remembered
            ? i18n.t('crm.ai_chat.actions.remembered')
            : i18n.t('crm.ai_chat.actions.remember')}
        </span>
      </button>

      {onRetry && (
        <button
          type="button"
          data-testid={`chat-message-regenerate-${messageId}`}
          disabled={disabled}
          onClick={onRetry}
          className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-stone-400 dark:text-stone-400 hover:bg-stone-200/60 dark:hover:bg-stone-800/80 hover:text-amber-800 transition cursor-pointer disabled:opacity-50"
        >
          <RotateCcw size={11} className="text-amber-600" />
          <span>{i18n.t('crm.ai_chat.actions.regenerate')}</span>
        </button>
      )}

      {onExecuteScenario && (
        <>
          {noteDeal !== undefined ? (
            <button
              type="button"
              data-testid={`chat-message-pin-note-${messageId}`}
              disabled={disabled}
              onClick={() =>
                executeResultAction(
                  'addDealNoteFromAnswer',
                  i18n.t('crm.ai_chat.actions.pin_note'),
                  i18n.t('crm.ai_chat.actions.pin_note_prompt'),
                  { type: 'deal', id: noteDeal.id, label: noteDeal.label },
                )
              }
              className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-stone-400 dark:text-stone-400 hover:bg-stone-200/60 dark:hover:bg-stone-800/80 hover:text-amber-800 transition cursor-pointer disabled:opacity-50"
            >
              <Pin size={11} className="text-amber-600" />
              <span>{i18n.t('crm.ai_chat.actions.pin_note')}</span>
            </button>
          ) : null}

          <button
            type="button"
            data-testid={`chat-message-create-tasks-${messageId}`}
            disabled={disabled}
            onClick={() =>
              executeResultAction(
                'createTasksFromAnswer',
                i18n.t('crm.ai_chat.actions.create_tasks'),
                i18n.t('crm.ai_chat.actions.create_tasks_prompt'),
                selectedEntity !== undefined &&
                  ['deal', 'contact', 'company', 'document'].includes(selectedEntity.type)
                  ? {
                      type: selectedEntity.type as 'deal' | 'contact' | 'company' | 'document',
                      id: selectedEntity.id,
                      label: selectedEntity.label,
                    }
                  : undefined,
              )
            }
            className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-stone-400 dark:text-stone-400 hover:bg-stone-200/60 dark:hover:bg-stone-800/80 hover:text-blue-800 transition cursor-pointer disabled:opacity-50"
          >
            <ListTodo size={11} className="text-blue-600" />
            <span>{i18n.t('crm.ai_chat.actions.create_tasks')}</span>
          </button>

          <button
            type="button"
            data-testid={`chat-message-dashboard-${messageId}`}
            disabled={disabled}
            onClick={() =>
              executeResultAction(
                'dashboardWidgetFromAnswer',
                i18n.t('crm.ai_chat.actions.dashboard'),
                i18n.t('crm.ai_chat.actions.dashboard_prompt'),
              )
            }
            className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-stone-400 dark:text-stone-400 hover:bg-stone-200/60 dark:hover:bg-stone-800/80 hover:text-amber-800 transition cursor-pointer disabled:opacity-50"
          >
            <LayoutDashboard size={11} className="text-amber-600" />
            <span>{i18n.t('crm.ai_chat.actions.dashboard')}</span>
          </button>
        </>
      )}
    </div>
  );
}

function UserMessageBubble({
  message,
  onEdit,
  disabled = false,
}: {
  message: Message;
  onEdit?: (() => void) | undefined;
  disabled?: boolean;
}): ReactElement {
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (copyTimerRef.current !== null) clearTimeout(copyTimerRef.current);
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      if (!mountedRef.current) return;
      setCopied(true);
      if (copyTimerRef.current !== null) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <div className="group relative pr-7 min-w-0">
      <span className="whitespace-pre-wrap break-words">{message.content}</span>
      <div className="absolute -bottom-1 right-0 flex items-center gap-0.5 opacity-0 transition-opacity duration-150 group-hover:opacity-100 bg-white/90 backdrop-blur-xs rounded-md shadow-2xs px-1 py-0.5 border border-stone-200/70">
        <button
          type="button"
          data-testid={`chat-message-copy-${message.id}`}
          onClick={() => void handleCopy()}
          title={i18n.t('crm.ai_chat.actions.copy')}
          className="inline-flex h-5 w-5 items-center justify-center rounded text-stone-400 dark:text-stone-400 hover:bg-stone-200/60 dark:hover:bg-stone-800/80 hover:text-stone-800 transition cursor-pointer"
        >
          {copied ? <Check size={11} className="text-emerald-600" /> : <Copy size={11} />}
        </button>
        {onEdit && (
          <button
            type="button"
            data-testid={`chat-message-edit-${message.id}`}
            disabled={disabled}
            onClick={onEdit}
            title={i18n.t('crm.ai_chat.actions.edit')}
            className="inline-flex h-5 w-5 items-center justify-center rounded text-stone-400 dark:text-stone-400 hover:bg-stone-200/60 dark:hover:bg-stone-800/80 hover:text-amber-800 transition cursor-pointer disabled:opacity-50"
          >
            <Pencil size={11} className="text-amber-600" />
          </button>
        )}
      </div>
    </div>
  );
}

function EditableUserMessage({
  message,
  onCommit,
  onCancel,
}: {
  message: Message;
  onCommit: (id: string, text: string) => void;
  onCancel: () => void;
}): ReactElement {
  const [value, setValue] = useState(message.content);
  return (
    <div className="min-w-0 space-y-1.5">
      <Input.TextArea
        data-testid={`chat-edit-input-${message.id}`}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        autoSize={{ minRows: 1, maxRows: 4 }}
        className="resize-none text-xs"
      />
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          data-testid={`chat-edit-save-${message.id}`}
          onClick={() => onCommit(message.id, value)}
          className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-900 transition hover:bg-amber-100 cursor-pointer"
        >
          <Check size={11} />
          {i18n.t('crm.ai_chat.actions.save')}
        </button>
        <button
          type="button"
          data-testid={`chat-edit-cancel-${message.id}`}
          onClick={onCancel}
          className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] text-stone-500 hover:bg-stone-100 cursor-pointer"
        >
          {i18n.t('crm.ai_chat.actions.cancel')}
        </button>
      </div>
    </div>
  );
}

function ReasoningBlock({
  content,
  isStreaming = false,
}: {
  content: string;
  isStreaming?: boolean;
}): ReactElement {
  const [userToggled, setUserToggled] = useState<boolean | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    if (!isStreaming) {
      return;
    }
    const start = Date.now();
    const interval = setInterval(() => {
      setElapsedSeconds(Math.max(1, Math.round((Date.now() - start) / 1000)));
    }, 1000);
    return () => clearInterval(interval);
  }, [isStreaming]);

  const isOpen = userToggled !== null ? userToggled : isStreaming;

  return (
    <div
      data-testid="reasoning-block"
      className="rounded-lg border border-zinc-200/70 bg-stone-50/70 overflow-hidden transition-all"
    >
      <button
        type="button"
        onClick={() => setUserToggled((prev) => (prev === null ? !isOpen : !prev))}
        className="flex w-full cursor-pointer items-center justify-between px-2.5 py-1.5 text-[11px] font-medium text-stone-500 transition hover:text-stone-700"
      >
        <div className="flex items-center gap-1.5">
          <Brain size={12} className={`text-amber-600 ${isStreaming ? 'animate-pulse' : ''}`} />
          <span>
            {isStreaming
              ? elapsedSeconds > 0
                ? i18n.t('crm.ai_chat.reasoning.thinking_elapsed', { seconds: elapsedSeconds })
                : i18n.t('crm.ai_chat.reasoning.thinking')
              : i18n.t(
                  'crm.features.features.ai.chat.components.reasoningmodeswitch.razmyshlenie_modeli',
                )}
          </span>
        </div>
        {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {isOpen && (
        <div className="max-h-40 overflow-y-auto border-t border-zinc-200/60 px-2.5 py-2 text-[11px] leading-relaxed text-stone-500 whitespace-pre-wrap crm-microscroll">
          {content}
        </div>
      )}
    </div>
  );
}

function ToolCallsExecution({
  toolCalls,
  isStreaming,
  statusById,
  approvalToolCallIds,
}: {
  toolCalls: ToolCall[];
  isStreaming: boolean;
  statusById: ReadonlyMap<string, 'ok' | 'error'>;
  approvalToolCallIds: ReadonlySet<string>;
}): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  // An approval-backed call already has its own card in the same turn, with the
  // same action and the decision buttons. The chip would repeat that action,
  // so it is rendered only for calls that execute without a confirmation card.
  const visibleToolCalls = toolCalls.filter((toolCall) => !approvalToolCallIds.has(toolCall.id));
  const isAnyRunning = visibleToolCalls.some((tc) => !statusById.has(tc.id)) && isStreaming;
  const errorCount = visibleToolCalls.filter(
    (toolCall) => statusById.get(toolCall.id) === 'error',
  ).length;

  if (visibleToolCalls.length === 0) {
    return <></>;
  }

  if (visibleToolCalls.length <= 3) {
    return (
      <div className="flex flex-wrap items-center gap-1.5 py-1">
        {visibleToolCalls.map((toolCall) => (
          <ToolCallPill
            key={toolCall.id}
            toolCall={toolCall}
            isStreaming={isStreaming}
            status={statusById.get(toolCall.id)}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="my-1 rounded-lg border border-zinc-200/60 bg-zinc-50/60 text-xs overflow-hidden">
      <div
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex items-center justify-between px-2.5 py-1.5 cursor-pointer hover:bg-zinc-100/60 transition select-none text-[11px] text-zinc-600 font-medium"
      >
        <div className="flex items-center gap-1.5">
          {isAnyRunning ? (
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" />
          ) : errorCount > 0 ? (
            <AlertTriangle size={12} className="text-rose-500" />
          ) : (
            <Check size={12} className="text-emerald-600" />
          )}
          <span>{i18n.t('crm.ai_chat.tool_calls.steps', { count: visibleToolCalls.length })}</span>
          {errorCount > 0 && (
            <span className="text-rose-600">
              · {i18n.t('crm.ai_chat.tool_calls.errors', { count: errorCount })}
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronUp size={12} className="text-zinc-400" />
        ) : (
          <ChevronDown size={12} className="text-zinc-400" />
        )}
      </div>

      {isOpen && (
        <div className="border-t border-zinc-200/50 p-2 space-y-1 bg-white">
          {visibleToolCalls.map((toolCall) => (
            <ToolCallPill
              key={toolCall.id}
              toolCall={toolCall}
              isStreaming={isStreaming}
              status={statusById.get(toolCall.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface MessageContentProps extends CrmActionPlanCallbacks {
  artifactDealId?: string | undefined;
  message: ChatUiMessage;
  isStreaming: boolean;
  onSendEmailDraft?: ((draft: EmailDraft) => void) | undefined;
  sendEmailDraftDisabled?: boolean;
  onPrepareDealFollowUp?: ((dealId: string) => void) | undefined;
  prepareDealFollowUpDisabled?: boolean;
  onProposeTouchStrategy?: ((dealId: string) => void) | undefined;
  onCreateDealTasks?:
    | ((plan: DealActionPlan, actions: DealActionPlan['actions']) => void)
    | undefined;
  createDealTasksDisabled?: boolean;
  createMeetingTasksDisabled?: boolean;
  onCreateMeetingTasks?:
    | ((meeting: MeetingOutcomeMeeting, actions: readonly MeetingTaskCandidate[]) => void)
    | undefined;
  prepareMeetingFollowUpDisabled?: boolean;
  onPrepareMeetingFollowUp?: ((meeting: MeetingOutcomeMeeting) => void) | undefined;
  onOpenCanvas?: ((artifact: ArtifactContent) => void) | undefined;
  onOpenEntity?: ((type: 'deal' | 'contact', id: string, title?: string) => void) | undefined;
  onExecutePrompt?: ((prompt: string) => void) | undefined;
  onExecuteScenario?:
    | ((
        request:
          | string
          | {
              prompt: string;
              command?: UiCommandRequest;
              attachmentId?: string;
              contextRefs?: ChatContextRef[];
            },
      ) => void)
    | undefined;
  onRequestModelUpgrade?:
    | ((offer: Extract<StructuredMessagePayload, { type: 'model_upgrade_offer' }>) => void)
    | undefined;
  onApprovalDecisionApplied?: ((approval: ApprovalItem) => void) | undefined;
  onRetry: (() => void) | undefined;
  canRetry?: boolean;
  editingMessageId: string | null;
  onStartEdit?: ((id: string) => void) | undefined;
  onCancelEdit: (() => void) | undefined;
  onCommitEdit: ((id: string, text: string) => void) | undefined;
  toolStatusById: ReadonlyMap<string, 'ok' | 'error'>;
  approvalToolCallIds: ReadonlySet<string>;
}

function MessageContent({
  message,
  artifactDealId,
  isStreaming,
  onSendEmailDraft,
  sendEmailDraftDisabled = false,
  onPrepareDealFollowUp,
  prepareDealFollowUpDisabled = false,
  onProposeTouchStrategy,
  onCreateDealTasks,
  createDealTasksDisabled = false,
  onCreateMeetingTasks,
  createMeetingTasksDisabled = false,
  onPrepareMeetingFollowUp,
  prepareMeetingFollowUpDisabled = false,
  onOpenCanvas,
  onOpenEntity,
  onExecutePrompt,
  onExecuteScenario,
  onRequestModelUpgrade,
  onApprovalDecisionApplied,
  onDecideActionPlanItem,
  onRequestActionPlanUndo,
  onRequestActionPlanRetry,
  onRetry,
  canRetry = false,
  editingMessageId = null,
  onStartEdit,
  onCancelEdit,
  onCommitEdit,
  toolStatusById,
  approvalToolCallIds,
}: MessageContentProps): ReactElement {
  useI18nSubscription();
  const sources = getSources(message);
  const shouldUseStructuredRenderer =
    message.role === 'tool' ||
    (message.structuredPayload !== undefined &&
      message.structuredPayload.type !== 'compound_clarification_v1' &&
      message.structuredPayload.type !== 'pending_batch_correction_v1' &&
      message.structuredPayload.type !== 'pending_task_correction_v1');
  const taskBoardDealId = extractTaskBoardDealId(message);

  return (
    <div
      className="space-y-2 min-w-0"
      data-message-id={message.id}
      id={`chat-message-${message.id}`}
    >
      {message.agentId !== undefined && message.agentId !== 'crm-chat-assistant' && (
        <AgentAttributionBadge agentId={message.agentId} />
      )}

      {message.toolCalls && message.toolCalls.length > 0 && (
        <ToolCallsExecution
          toolCalls={message.toolCalls}
          isStreaming={isStreaming}
          statusById={toolStatusById}
          approvalToolCallIds={approvalToolCallIds}
        />
      )}

      {message.role === 'assistant' && taskBoardDealId !== undefined && (
        <TaskKanbanChatSnippet dealId={taskBoardDealId} />
      )}

      {message.error ? (
        <div
          data-testid="chat-error-bubble"
          className="rounded-lg border border-rose-200 bg-rose-50/70 px-3 py-2.5 text-xs"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0 text-rose-500" aria-hidden="true" />
            <div className="min-w-0 space-y-1">
              <p className="font-semibold text-rose-900">{i18n.t('crm.ai_chat.error.title')}</p>
              {/* PR 5 file inputs: typed attachment failures (legacy office,
                  images, empty content) localize by machine code instead of
                  surfacing the raw server envelope. */}
              <p className="text-rose-800">
                {localizedFileInputError(message.error.code, i18n.t) ?? message.error.message}
              </p>
              {message.error.code !== undefined && (
                // Keep a bracket-free class first: jsdom's nwsapi builds
                // :scope selectors from classList[0] and fails on brackets.
                <p className="font-medium text-rose-500 text-[11px]">code: {message.error.code}</p>
              )}
              {message.error.traceId !== undefined && (
                <p className="font-mono text-[10px] text-rose-400">{message.error.traceId}</p>
              )}
            </div>
          </div>
          {!isStreaming && canRetry && onRetry !== undefined && (
            <button
              type="button"
              data-testid="chat-error-retry"
              onClick={onRetry}
              className="mt-2 inline-flex items-center gap-1 rounded-md border border-rose-200 bg-white px-2 py-1 text-[11px] font-medium text-rose-700 transition hover:bg-rose-50 cursor-pointer"
            >
              <RotateCcw size={11} aria-hidden="true" />
              {i18n.t('crm.ai_chat.error.retry')}
            </button>
          )}
        </div>
      ) : shouldUseStructuredRenderer ? (
        <StructuredMessageRenderer
          artifactDealId={artifactDealId}
          message={message}
          onSendEmailDraft={onSendEmailDraft}
          sendEmailDraftDisabled={sendEmailDraftDisabled}
          onPrepareDealFollowUp={onPrepareDealFollowUp}
          prepareDealFollowUpDisabled={prepareDealFollowUpDisabled}
          onProposeTouchStrategy={onProposeTouchStrategy}
          onCreateDealTasks={onCreateDealTasks}
          createDealTasksDisabled={createDealTasksDisabled}
          onCreateMeetingTasks={onCreateMeetingTasks}
          createMeetingTasksDisabled={createMeetingTasksDisabled}
          onPrepareMeetingFollowUp={onPrepareMeetingFollowUp}
          prepareMeetingFollowUpDisabled={prepareMeetingFollowUpDisabled}
          onOpenCanvas={onOpenCanvas}
          onOpenEntity={onOpenEntity}
          onExecutePrompt={onExecutePrompt}
          onExecuteScenario={onExecuteScenario}
          onRequestModelUpgrade={onRequestModelUpgrade}
          onApprovalDecisionApplied={onApprovalDecisionApplied}
          onDecideActionPlanItem={onDecideActionPlanItem}
          onRequestActionPlanUndo={onRequestActionPlanUndo}
          onRequestActionPlanRetry={onRequestActionPlanRetry}
        />
      ) : message.role === 'assistant' ? (
        <>
          {message.reasoning !== undefined && message.reasoning.length > 0 && (
            <ReasoningBlock
              content={message.reasoning}
              isStreaming={isStreaming && message.content.length === 0}
            />
          )}
          {message.content.length === 0 && isStreaming ? (
            <div className="flex items-center gap-1.5 py-1 px-1">
              <Brain size={12} className="animate-pulse text-stone-400" aria-hidden="true" />
              <span className="animate-pulse text-[11px] font-medium text-stone-400">
                {i18n.t(
                  'crm.features.features.ai.chat.components.antdesignxchatsurface.otvet_formiruetsya',
                )}
              </span>
            </div>
          ) : (
            <BoundedMarkdownRenderer
              content={sanitizeChatProse(message.content)}
              isStreaming={isStreaming}
            />
          )}
        </>
      ) : message.role === 'user' && editingMessageId === message.id ? (
        <EditableUserMessage
          message={message}
          onCommit={onCommitEdit ?? (() => undefined)}
          onCancel={onCancelEdit ?? (() => undefined)}
        />
      ) : message.role === 'user' ? (
        <UserMessageBubble
          message={message}
          onEdit={onStartEdit ? () => onStartEdit(message.id) : undefined}
          disabled={isStreaming}
        />
      ) : (
        <span>{sanitizeChatProse(message.content)}</span>
      )}

      {sources.length > 0 ? (
        <Sources
          items={sources}
          title={i18n.t(
            'crm.features.features.ai.chat.components.antdesignxchatsurface.istochniki',
          )}
          inline
        />
      ) : null}
      {message.role === 'assistant' && message.routeDiagnostic !== undefined && (
        <p
          data-testid="chat-provider-route"
          className="font-mono text-[10px] text-stone-400"
          title={message.traceId}
        >
          {i18n.t('crm.ai_chat.route.used', {
            provider: i18n.t(`crm.ai_chat.route.provider.${message.routeDiagnostic.provider}`),
            model: message.routeDiagnostic.resolvedModel,
            trace: message.traceId ?? '—',
          })}
        </p>
      )}
      {message.role === 'assistant' &&
        isStreaming &&
        message.routeDiagnostic === undefined &&
        message.requestedRoute !== undefined && (
          <p data-testid="chat-requested-route" className="font-mono text-[10px] text-stone-400">
            {i18n.t('crm.ai_chat.route.requested', {
              provider: i18n.t(`crm.ai_chat.route.provider.${message.requestedRoute.provider}`),
              model: message.requestedRoute.model,
            })}
          </p>
        )}
    </div>
  );
}

function shouldIncludeInBubbleList(message: Message): boolean {
  if (message.role === 'tool') {
    if (message.structuredPayload !== undefined) {
      return true;
    }
    if (parseApprovalCardEntry(message, i18n.t) !== null) {
      return true;
    }
    const trimmed = message.content.trim();
    if (!trimmed) {
      return false;
    }
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        JSON.parse(trimmed);
        return false;
      } catch {
        return true;
      }
    }
  }
  return true;
}

type GroupedBubbleEntry =
  | { kind: 'single'; message: ChatUiMessage }
  | { kind: 'grouped'; payloadType: 'deal_risk' | 'deal' | 'contact'; messages: ChatUiMessage[] };

function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  if (value !== null && typeof value === 'object') {
    return `{${Object.entries(value)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => `${JSON.stringify(key)}:${canonicalJson(item)}`)
      .join(',')}}`;
  }
  return JSON.stringify(value) ?? 'undefined';
}

function isHiddenCompoundPlanResult(message: ChatUiMessage): boolean {
  if (message.role !== 'tool' || message.toolCallId !== 'server-owned:compound-plan') return false;
  try {
    const parsed: unknown = JSON.parse(message.content);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return false;
    const record = parsed as Record<string, unknown>;
    const output =
      record['output'] !== null &&
      typeof record['output'] === 'object' &&
      !Array.isArray(record['output'])
        ? (record['output'] as Record<string, unknown>)
        : record;
    const plan = output['plan'];
    return (
      output['type'] === 'crm_action_plan_v1' &&
      plan !== null &&
      typeof plan === 'object' &&
      !Array.isArray(plan) &&
      typeof (plan as Record<string, unknown>)['id'] === 'string' &&
      Array.isArray((plan as Record<string, unknown>)['items'])
    );
  } catch {
    return false;
  }
}

function groupBubbleMessages(messages: ChatUiMessage[], isStreaming = false): GroupedBubbleEntry[] {
  const approvalCallIds = new Set(
    messages.flatMap((message) =>
      message.role === 'tool' &&
      message.toolCallId &&
      (parseApprovalCardEntry(message, i18n.t) || isHiddenCompoundPlanResult(message))
        ? [message.toolCallId]
        : [],
    ),
  );
  const visible = messages.filter((message, index) => {
    if (!shouldIncludeInBubbleList(message)) return false;
    // Keep protocol rows in history; suppress only their empty visual shell.
    if (
      message.role !== 'assistant' ||
      message.content.trim() !== '' ||
      message.structuredPayload !== undefined ||
      (message.reasoning?.trim() ?? '') !== '' ||
      message.error !== undefined ||
      message.routeDiagnostic !== undefined ||
      message.requestedRoute !== undefined ||
      (isStreaming && index === messages.length - 1)
    )
      return true;
    return (message.toolCalls ?? []).some((call) => !approvalCallIds.has(call.id));
  });
  const latestPendingBySignature = new Map<string, number>();

  visible.forEach((message, index) => {
    const entry = parseApprovalCardEntry(message, i18n.t);
    if (entry?.approval.status !== 'pending') return;
    latestPendingBySignature.set(canonicalJson(entry.approval.payload), index);
  });

  const withoutSupersededPending = visible.filter((message, index) => {
    const entry = parseApprovalCardEntry(message, i18n.t);
    if (entry?.approval.status !== 'pending') return true;
    return latestPendingBySignature.get(canonicalJson(entry.approval.payload)) === index;
  });

  // A provider emits the approval tool event before its explanatory assistant
  // message. Keep every approval state after that explanation. Otherwise a
  // hydrated approved card can appear above the historical "please confirm"
  // sentence and look like a second confirmation request.
  const filtered: Message[] = [];
  let approvalCards: Message[] = [];
  for (const message of withoutSupersededPending) {
    const approval = parseApprovalCardEntry(message, i18n.t);
    if (approval !== null) {
      approvalCards.push(message);
      continue;
    }
    if (message.role === 'assistant' && approvalCards.length > 0) {
      filtered.push(message, ...approvalCards);
      approvalCards = [];
      continue;
    }
    if (message.role === 'user' && approvalCards.length > 0) {
      filtered.push(...approvalCards);
      approvalCards = [];
    }
    filtered.push(message);
  }
  filtered.push(...approvalCards);
  const result: GroupedBubbleEntry[] = [];
  let i = 0;

  while (i < filtered.length) {
    const current = filtered[i];
    const payloadType = current?.structuredPayload?.type;

    if (
      current?.role === 'tool' &&
      (payloadType === 'deal_risk' || payloadType === 'deal' || payloadType === 'contact')
    ) {
      const group: Message[] = [current];
      let j = i + 1;
      while (
        j < filtered.length &&
        filtered[j]?.role === 'tool' &&
        filtered[j]?.structuredPayload?.type === payloadType
      ) {
        group.push(filtered[j]!);
        j++;
      }

      if (group.length > 1) {
        result.push({ kind: 'grouped', payloadType, messages: group });
        i = j;
        continue;
      }
    }

    result.push({ kind: 'single', message: current! });
    i++;
  }

  return result;
}

function buildBubbleItems(
  messages: ChatUiMessage[],
  isStreaming: boolean,
  onSendEmailDraft: ((draft: EmailDraft) => void) | undefined,
  sendEmailDraftDisabled: boolean,
  onPrepareDealFollowUp: ((dealId: string) => void) | undefined,
  prepareDealFollowUpDisabled: boolean,
  onProposeTouchStrategy: ((dealId: string) => void) | undefined,
  onCreateDealTasks:
    | ((plan: DealActionPlan, actions: DealActionPlan['actions']) => void)
    | undefined,
  createDealTasksDisabled: boolean,
  onCreateMeetingTasks:
    | ((meeting: MeetingOutcomeMeeting, actions: readonly MeetingTaskCandidate[]) => void)
    | undefined,
  createMeetingTasksDisabled: boolean,
  onPrepareMeetingFollowUp: ((meeting: MeetingOutcomeMeeting) => void) | undefined,
  prepareMeetingFollowUpDisabled: boolean,
  onOpenCanvas?: ((artifact: ArtifactContent) => void) | undefined,
  onOpenEntity?: ((type: 'deal' | 'contact', id: string, title?: string) => void) | undefined,
  onExecutePrompt?: ((prompt: string) => void) | undefined,
  onExecuteScenario?:
    | ((
        request:
          | string
          | {
              prompt: string;
              command?: UiCommandRequest;
              attachmentId?: string;
              contextRefs?: ChatContextRef[];
            },
      ) => void)
    | undefined,
  onRequestModelUpgrade?:
    | ((offer: Extract<StructuredMessagePayload, { type: 'model_upgrade_offer' }>) => void)
    | undefined,
  onApprovalDecisionApplied?: ((approval: ApprovalItem) => void) | undefined,
  onDecideActionPlanItem?: CrmActionPlanCallbacks['onDecideActionPlanItem'],
  onRequestActionPlanUndo?: CrmActionPlanCallbacks['onRequestActionPlanUndo'],
  onRequestActionPlanRetry?: CrmActionPlanCallbacks['onRequestActionPlanRetry'],
  onRetry?: (() => void) | undefined,
  canRetry?: boolean,
  editingMessageId?: string | null,
  onStartEdit?: ((id: string) => void) | undefined,
  onCancelEdit?: (() => void) | undefined,
  onCommitEdit?: ((id: string, text: string) => void) | undefined,
  contextEntityType?: 'deal' | 'contact' | 'project' | undefined,
  contextEntityId?: string | undefined,
  selectedEntity?:
    | {
        type: Exclude<ChatContextRef['type'], 'command' | 'assistant_message' | 'user'>;
        id: string;
        label: string;
        inferred: boolean;
      }
    | undefined,
): BubbleItemType[] {
  const groupedEntries = groupBubbleMessages(
    messages.map((message) => {
      const command = (message as typeof message & { uiCommand?: UiCommandRequest }).uiCommand;
      return command ? { ...message, content: commandLabel(command, i18n.locale) } : message;
    }),
    isStreaming,
  );
  const latestRetryableErrorId = [...messages]
    .reverse()
    .find((message) => message.error !== undefined)?.id;
  const latestAssistantAnswerId = [...messages]
    .reverse()
    .find(
      (message) =>
        message.role === 'assistant' && message.error === undefined && message.content.length > 0,
    )?.id;

  const toolStatusById = new Map<string, 'ok' | 'error'>();
  const approvalToolCallIds = new Set<string>();
  for (const message of messages) {
    if (message.role !== 'tool' || message.toolCallId === undefined) {
      continue;
    }
    if (parseApprovalCardEntry(message, i18n.t) !== null || isHiddenCompoundPlanResult(message)) {
      approvalToolCallIds.add(message.toolCallId);
    }
    const trimmed = message.content.trim();
    let failed = false;
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const parsed: unknown = JSON.parse(trimmed);
        if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
          const record = parsed as Record<string, unknown>;
          const nestedOutput =
            record['output'] !== null &&
            typeof record['output'] === 'object' &&
            !Array.isArray(record['output'])
              ? (record['output'] as Record<string, unknown>)
              : undefined;
          failed = record['ok'] === false || nestedOutput?.['ok'] === false;
        }
      } catch {
        failed = false;
      }
    }
    toolStatusById.set(message.toolCallId, failed ? 'error' : 'ok');
  }

  return groupedEntries.map((entry, index, arr) => {
    if (entry.kind === 'grouped') {
      const firstMessage = entry.messages[0]!;
      const count = entry.messages.length;
      const title =
        entry.payloadType === 'deal_risk'
          ? i18n.t('crm.chat.deal_risk.carousel_title')
          : entry.payloadType === 'deal'
            ? i18n.t('crm.chat.deal.carousel_title')
            : i18n.t('crm.chat.contact.carousel_title');

      return {
        key: `grouped-${entry.payloadType}-${firstMessage.id}`,
        role: toBubbleRole(firstMessage),
        content: '',
        classNames: {
          root: 'ack-chat-panel__message ack-chat-panel__message--tool',
        },
        contentRender: () => (
          <div data-chat-message-id={firstMessage.id}>
            <LobeEntityFeed title={title} count={count} payloadType={entry.payloadType}>
              {entry.messages.map((msg) => (
                <StructuredMessageRenderer
                  artifactDealId={
                    contextEntityType === 'deal'
                      ? contextEntityId
                      : selectedEntity?.type === 'deal'
                        ? selectedEntity.id
                        : undefined
                  }
                  key={msg.id}
                  message={msg}
                  compact
                  onSendEmailDraft={onSendEmailDraft}
                  sendEmailDraftDisabled={sendEmailDraftDisabled}
                  onPrepareDealFollowUp={onPrepareDealFollowUp}
                  prepareDealFollowUpDisabled={prepareDealFollowUpDisabled}
                  onProposeTouchStrategy={onProposeTouchStrategy}
                  onCreateDealTasks={onCreateDealTasks}
                  createDealTasksDisabled={createDealTasksDisabled}
                  onCreateMeetingTasks={onCreateMeetingTasks}
                  createMeetingTasksDisabled={createMeetingTasksDisabled}
                  onPrepareMeetingFollowUp={onPrepareMeetingFollowUp}
                  prepareMeetingFollowUpDisabled={prepareMeetingFollowUpDisabled}
                  onOpenCanvas={onOpenCanvas}
                  onOpenEntity={onOpenEntity}
                  onExecutePrompt={onExecutePrompt}
                  onExecuteScenario={onExecuteScenario}
                  onRequestModelUpgrade={onRequestModelUpgrade}
                  onApprovalDecisionApplied={onApprovalDecisionApplied}
                  onDecideActionPlanItem={onDecideActionPlanItem}
                  onRequestActionPlanUndo={onRequestActionPlanUndo}
                  onRequestActionPlanRetry={onRequestActionPlanRetry}
                />
              ))}
            </LobeEntityFeed>
          </div>
        ),
      };
    }

    const message = entry.message;
    const isActiveStream = isStreaming && index === arr.length - 1 && message.role === 'assistant';

    return {
      key: message.id,
      role: toBubbleRole(message),
      content: message.content,
      // No `loading`: a loading bubble replaces contentRender with the bare
      // default dot spinner, so the waiting indicator below never rendered.
      streaming: isActiveStream,
      classNames: {
        root: `ack-chat-panel__message ack-chat-panel__message--${message.role} ${
          isActiveStream ? 'crm-streaming-bubble border-amber-300' : ''
        }`,
      },
      contentRender: () => (
        <div data-chat-message-id={message.id}>
          <MessageContent
            artifactDealId={
              contextEntityType === 'deal'
                ? contextEntityId
                : selectedEntity?.type === 'deal'
                  ? selectedEntity.id
                  : undefined
            }
            message={message}
            isStreaming={isActiveStream}
            onSendEmailDraft={onSendEmailDraft}
            sendEmailDraftDisabled={sendEmailDraftDisabled}
            onPrepareDealFollowUp={onPrepareDealFollowUp}
            prepareDealFollowUpDisabled={prepareDealFollowUpDisabled}
            onProposeTouchStrategy={onProposeTouchStrategy}
            onCreateDealTasks={onCreateDealTasks}
            createDealTasksDisabled={createDealTasksDisabled}
            onCreateMeetingTasks={onCreateMeetingTasks}
            createMeetingTasksDisabled={createMeetingTasksDisabled}
            onPrepareMeetingFollowUp={onPrepareMeetingFollowUp}
            prepareMeetingFollowUpDisabled={prepareMeetingFollowUpDisabled}
            onOpenCanvas={onOpenCanvas}
            onOpenEntity={onOpenEntity}
            onExecutePrompt={onExecutePrompt}
            onExecuteScenario={onExecuteScenario}
            onRequestModelUpgrade={onRequestModelUpgrade}
            onApprovalDecisionApplied={onApprovalDecisionApplied}
            onDecideActionPlanItem={onDecideActionPlanItem}
            onRequestActionPlanUndo={onRequestActionPlanUndo}
            onRequestActionPlanRetry={onRequestActionPlanRetry}
            onRetry={onRetry}
            canRetry={(canRetry ?? false) && message.id === latestRetryableErrorId}
            editingMessageId={editingMessageId ?? null}
            onStartEdit={onStartEdit}
            onCancelEdit={onCancelEdit}
            onCommitEdit={onCommitEdit}
            toolStatusById={toolStatusById}
            approvalToolCallIds={approvalToolCallIds}
          />
        </div>
      ),
      footer:
        message.role === 'assistant' && message.error === undefined && message.content.length > 0
          ? () => (
              <AssistantMessageFooter
                messageId={message.id}
                content={message.content}
                onExecuteScenario={onExecuteScenario}
                onRetry={message.id === latestAssistantAnswerId ? onRetry : undefined}
                disabled={isStreaming}
                contextEntityType={contextEntityType}
                contextEntityId={contextEntityId}
                selectedEntity={selectedEntity}
                allowInferredDealNote={message.id === latestAssistantAnswerId}
              />
            )
          : undefined,
    };
  });
}

export function AntDesignXChatSurface({
  agent,
  placeholder,
  submitLabel,
  disabled = false,
  conversationId,
  onEnsureConversation,
  draftScope,
  onSendEmailDraft,
  onPrepareDealFollowUp,
  onProposeTouchStrategy,
  onCreateDealTasks,
  onCreateMeetingTasks,
  createMeetingTasksDisabled = false,
  onPrepareMeetingFollowUp,
  prepareMeetingFollowUpDisabled = false,
  onOpenCanvas,
  onOpenEntity,
  onExecutePrompt,
  onExecuteScenario,
  onRequestModelUpgrade,
  onApprovalDecisionApplied,
  onDecideActionPlanItem,
  onRequestActionPlanUndo,
  onRequestActionPlanRetry,
  contextEntityType,
  contextEntityId,
  onContextRefsChange,
}: AntDesignXChatSurfaceProps): ReactElement {
  useI18nSubscription();
  const locale = i18n.locale;
  const [draft, setDraft] = useState('');
  const [entityChips, setEntityChips] = useState<ChatEntityChip[]>([]);
  const [isListening, setIsListening] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [pendingFileAttachment, setPendingFileAttachment] = useState<PendingFileAttachment | null>(
    null,
  );
  // True while a picked file is being uploaded (including the conversation
  // creation that a fresh /chat needs first): drives the paperclip spinner.
  const [isAttachingFile, setIsAttachingFile] = useState(false);
  const draftTouchedRef = useRef(false);
  const entityChipsRef = useRef<ChatEntityChip[]>([]);
  entityChipsRef.current = entityChips;
  useEffect(() => {
    onContextRefsChange?.(entityChips);
  }, [entityChips, onContextRefsChange]);
  // Read at send time so `sendWithEntityChips` picks the pending file without
  // rebuilding its identity on every attach/detach.
  const pendingFileAttachmentRef = useRef<PendingFileAttachment | null>(null);
  pendingFileAttachmentRef.current = pendingFileAttachment;
  // Synchronous re-entrancy guard: the file input, drag-and-drop and paste can
  // all start an upload while a previous one is still resolving.
  const isAttachingFileRef = useRef(false);
  // Latest conversation id, read when an upload resolves: the closure value
  // captured at pick time is stale after several renders.
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;
  // Conversation created on demand by a paperclip upload in a fresh chat. Its
  // first switch must not wipe the composer the file was picked from.
  const attachTargetConversationIdRef = useRef<string | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  // Leaving the page mid-dictation must not keep the microphone session hot.
  useEffect(
    () => () => {
      recognitionRef.current?.stop();
      recognitionRef.current = null;
    },
    [],
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);
  const executeScenario = useCallback(
    (
      request:
        | string
        | {
            prompt: string;
            command?: UiCommandRequest;
            attachmentId?: string;
            contextRefs?: ChatContextRef[];
          },
    ): void => {
      if (onExecuteScenario !== undefined) {
        void Promise.resolve(onExecuteScenario(request)).catch(() => undefined);
        return;
      }
      const scenario = typeof request === 'string' ? { prompt: request } : request;
      void agent
        .send(scenario.command ?? scenario.prompt, {
          ...(scenario.contextRefs === undefined ? {} : { contextRefs: scenario.contextRefs }),
        })
        .catch(() => undefined);
    },
    [agent, onExecuteScenario],
  );
  const selectedEntity =
    (contextEntityType === 'deal' || contextEntityType === 'contact') &&
    contextEntityId !== undefined
      ? {
          type: contextEntityType,
          id: contextEntityId,
          label:
            agent.selectedEntity?.type === contextEntityType
              ? agent.selectedEntity.label
              : contextEntityType === 'deal'
                ? (agent.selectedDeal?.label ?? contextEntityId)
                : contextEntityId,
          inferred: false,
        }
      : agent.selectedEntity !== null && agent.selectedEntity !== undefined
        ? { ...agent.selectedEntity, inferred: true }
        : agent.selectedDeal === null || agent.selectedDeal === undefined
          ? undefined
          : { type: 'deal' as const, ...agent.selectedDeal, inferred: true };

  const canPersistDraft =
    conversationId !== undefined && conversationId !== null && draftScope !== undefined;

  useEffect(() => {
    // A paperclip upload that just created its conversation on a fresh /chat
    // switches conversationId from null to the new id. That switch is not a
    // user navigation: the typed draft, entity chips and pending file were
    // collected before the id existed and must survive into the new
    // conversation instead of being reset to its (empty) stored draft.
    if (
      conversationId !== undefined &&
      conversationId !== null &&
      conversationId === attachTargetConversationIdRef.current
    ) {
      return;
    }
    draftTouchedRef.current = false;
    if (conversationId === undefined || conversationId === null || draftScope === undefined) {
      setDraft('');
      setEntityChips([]);
      setPendingFileAttachment(null);
      return;
    }
    const storedDraft = readStoredDraft(draftScope, conversationId);
    setDraft(storedDraft.text);
    setEntityChips(storedDraft.contextRefs);
    // A pending file is owned by the conversation it was uploaded into; it
    // must never leak into a different conversation's next message.
    setPendingFileAttachment(null);
  }, [conversationId, draftScope?.userId, draftScope?.workspaceId]);

  useEffect(() => {
    if (
      !canPersistDraft ||
      conversationId === undefined ||
      conversationId === null ||
      draftScope === undefined ||
      !draftTouchedRef.current
    ) {
      return;
    }
    writeStoredDraft(draftScope, conversationId, { text: draft, contextRefs: entityChips });
  }, [canPersistDraft, conversationId, draft, draftScope, entityChips]);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const prependAnchorRef = useRef<{
    element: HTMLElement | null;
    offset: number;
    top: number;
    height: number;
  } | null>(null);
  const [historyScrollPaused, setHistoryScrollPaused] = useState(false);
  const lastHistoryMessageIdRef = useRef<string | undefined>(undefined);
  const autoScrolledApprovalIdsRef = useRef(new Set<string>());

  const handleToggleVoice = (): void => {
    const SpeechRecognition = resolveSpeechRecognition(window);
    if (!SpeechRecognition) {
      void message.warning(i18n.t('crm.ai_chat.voice_input.unsupported'));
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    try {
      // База фиксируется в момент старта: каждый onresult отдаёт ПОЛНУЮ
      // гипотезу (interimResults=true), поэтому draft заменяется, а не
      // дополняется — иначе полный текст копился бы в себя же.
      const baseDraft = draft.trim();
      const recognition = startRecognition({
        locale: i18n.locale,
        constructor: SpeechRecognition,
        callbacks: {
          onStart: () => setIsListening(true),
          onResult: (transcript) => {
            setDraft(baseDraft ? `${baseDraft} ${transcript}` : transcript);
          },
          onError: () => setIsListening(false),
          onEnd: () => setIsListening(false),
        },
      });
      recognitionRef.current = recognition;
    } catch {
      setIsListening(false);
    }
  };

  const addEntityChip = (chip: ChatEntityChip): void => {
    draftTouchedRef.current = true;
    setEntityChips((current) => {
      if (current.length >= DEFAULT_ENTITY_CHIP_MAX) {
        return current;
      }
      const alreadyAttached = current.some(
        (existing) => existing.type === chip.type && existing.id === chip.id,
      );
      return alreadyAttached ? current : [...current, chip];
    });
  };

  const removeEntityChip = (chip: ChatEntityChip): void => {
    draftTouchedRef.current = true;
    setEntityChips((current) =>
      current.filter((existing) => existing.type !== chip.type || existing.id !== chip.id),
    );
  };

  const handleFileSelect = async (file: File): Promise<void> => {
    if (isAttachingFileRef.current) {
      return;
    }
    const entityType =
      contextEntityType === 'deal' || contextEntityType === 'contact' ? contextEntityType : null;
    isAttachingFileRef.current = true;
    setIsAttachingFile(true);
    try {
      if (entityType && contextEntityId) {
        // Entity cards keep the entity-owned path: the file lands in the
        // deal/contact attachment panel and rides the message as a context
        // ref chip.
        const attachment = await uploadAttachment(entityType, contextEntityId, file);
        addEntityChip({ type: 'attachment', id: attachment.id, label: attachment.originalName });
        return;
      }
      // PR 5: the general /chat owns its files through the conversation
      // itself — no entity context is required anymore. The file attaches to
      // the next outgoing message instead of a context ref.
      let targetConversationId = conversationId ?? null;
      if (targetConversationId === null) {
        // A conversation that has no server row yet (fresh /chat) is created
        // on demand by the parent, through the same call the first message
        // uses — otherwise the paperclip dead-ended on a chat that had not
        // sent anything yet.
        if (onEnsureConversation === undefined) {
          void message.info(i18n.t('crm.ai_chat.file_input.conversation_required'));
          return;
        }
        targetConversationId = await onEnsureConversation();
        if (targetConversationId === null || targetConversationId === undefined) {
          void message.error(i18n.t('crm.ai_chat.error.upload_failed'));
          return;
        }
        attachTargetConversationIdRef.current = targetConversationId;
      }
      const attachment = await uploadAttachment('conversation', targetConversationId, file);
      // The upload can outlive a manual conversation switch: a file owned by
      // another conversation must not ride this composer's next message.
      if (conversationIdRef.current === targetConversationId) {
        setPendingFileAttachment({ id: attachment.id, fileName: attachment.originalName });
      }
    } catch {
      // Without the toast a failed upload looked exactly like a slow one: the
      // paperclip stayed clickable and no attachment chip ever appeared.
      void message.error(i18n.t('crm.ai_chat.error.upload_failed'));
    } finally {
      isAttachingFileRef.current = false;
      setIsAttachingFile(false);
      attachTargetConversationIdRef.current = null;
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      void handleFileSelect(file);
    }
    event.target.value = '';
  };

  // Drag-and-drop handlers for file upload
  const handleDragEnter = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;
    const file = e.dataTransfer.files?.[0];
    if (file) {
      void handleFileSelect(file);
    }
  };

  // Paste handler for images/screenshots
  const handlePaste = (e: React.ClipboardEvent): void => {
    const files = e.clipboardData?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file) {
        e.preventDefault();
        void handleFileSelect(file);
      }
    }
  };

  // Stable identity keeps the bubbleItems memo from rebuilding the whole
  // message list on every composer keystroke.
  const sendWithEntityChips = useCallback(
    (text: string, contextRefs = entityChipsRef.current): Promise<void> => {
      // The send guard inside useEntityChat silently drops the turn while the
      // workspace scope resolves; a dead click must say why instead.
      if (!agent.scopeReady) {
        void message.info(i18n.t('crm.ai_chat.error.not_ready'));
        return Promise.resolve();
      }
      if (agent.isStreaming) {
        void message.info(i18n.t('crm.ai_chat.error.busy'));
        return Promise.resolve();
      }
      const pendingAttachment = pendingFileAttachmentRef.current;
      // A pending conversation file only travels with a direct agent turn:
      // the scenario/prompt callback path has no attachment channel. Чипы
      // дополняют панельный контекст: чтобы не потерять их, отправляем
      // напрямую в агента (mergeContextRefs в useEntityChat объединит их с
      // контекст-рефом карточки).
      if (contextRefs.length > 0 || pendingAttachment !== null) {
        return agent.send(text, {
          ...(contextRefs.length === 0 ? {} : { contextRefs }),
          ...(pendingAttachment === null ? {} : { attachmentId: pendingAttachment.id }),
        });
      }
      if (onExecutePrompt) {
        return onExecutePrompt(text);
      }
      return agent.send(text);
    },
    [agent.send, agent.scopeReady, agent.isStreaming, onExecutePrompt],
  );

  const bubbleItems = useMemo(
    () =>
      buildBubbleItems(
        agent.messages as ChatUiMessage[],
        agent.isStreaming,
        onSendEmailDraft,
        disabled || agent.isStreaming,
        onPrepareDealFollowUp,
        disabled || agent.isStreaming,
        onProposeTouchStrategy,
        onCreateDealTasks,
        disabled || agent.isStreaming,
        onCreateMeetingTasks,
        createMeetingTasksDisabled || agent.isStreaming,
        onPrepareMeetingFollowUp,
        prepareMeetingFollowUpDisabled || agent.isStreaming,
        onOpenCanvas,
        onOpenEntity,
        onExecutePrompt,
        executeScenario,
        onRequestModelUpgrade,
        onApprovalDecisionApplied,
        onDecideActionPlanItem,
        onRequestActionPlanUndo,
        onRequestActionPlanRetry,
        agent.canRetryLastRequest === true ? () => void agent.retryLastRequest?.() : undefined,
        agent.canRetryLastRequest === true,
        editingMessageId,
        (id) => setEditingMessageId(id),
        () => setEditingMessageId(null),
        (_id, text) => {
          setEditingMessageId(null);
          const next = text.trim();
          if (!next || agent.isStreaming) {
            return;
          }
          // A failed resend surfaces as the turn error state in the bubble
          // list; the catch only keeps the rejected promise from going
          // unhandled.
          void sendWithEntityChips(next).catch(() => {});
        },
        contextEntityType,
        contextEntityId,
        selectedEntity,
      ),
    [
      agent.canRetryLastRequest,
      agent.isStreaming,
      agent.messages,
      agent.retryLastRequest,
      disabled,
      editingMessageId,
      onCreateDealTasks,
      onCreateMeetingTasks,
      createMeetingTasksDisabled,
      onPrepareMeetingFollowUp,
      prepareMeetingFollowUpDisabled,
      onPrepareDealFollowUp,
      onProposeTouchStrategy,
      onSendEmailDraft,
      onOpenCanvas,
      onOpenEntity,
      onExecutePrompt,
      executeScenario,
      onRequestModelUpgrade,
      onApprovalDecisionApplied,
      onDecideActionPlanItem,
      onRequestActionPlanUndo,
      onRequestActionPlanRetry,
      contextEntityType,
      contextEntityId,
      selectedEntity,
      sendWithEntityChips,
    ],
  );

  const pendingApprovalIds = useMemo(
    () =>
      new Set(
        agent.pendingApprovals
          .filter((approval) => approval.status === 'pending')
          .map((approval) => approval.id),
      ),
    [agent.pendingApprovals],
  );

  const senderComponents = useMemo(() => ({ input: TestableTextArea }), []);

  const suggestions = useMemo<{ id: string; label: string; action: UiCommandRequest }[]>(
    () => [
      {
        id: 'sug-leads',
        label: i18n.t('crm.ai_chat.suggestions.hot_leads'),
        action: commandRequest('scenarioHotLeads'),
      },
      {
        id: 'sug-risk',
        label: i18n.t('crm.ai_chat.suggestions.risk_audit'),
        action: commandRequest('scenarioDealRisks'),
      },
      {
        id: 'sug-followup',
        label: i18n.t('crm.ai_chat.suggestions.follow_up'),
        action: commandRequest('scenarioFollowUp'),
      },
      {
        id: 'sug-tasks',
        label: i18n.t('crm.ai_chat.suggestions.daily_tasks'),
        action: commandRequest('scenarioDailyPlan'),
      },
    ],
    [locale],
  );

  const clearComposerDraft = (): void => {
    if (canPersistDraft && conversationId !== undefined && conversationId !== null && draftScope) {
      writeStoredDraft(draftScope, conversationId, EMPTY_STORED_DRAFT);
    }
    draftTouchedRef.current = false;
    setDraft('');
    setEntityChips([]);
  };

  const handleSelectPrompt = (command: UiCommandRequest): void => {
    if (!agent.scopeReady) {
      void message.info(i18n.t('crm.ai_chat.error.not_ready'));
      return;
    }
    if (agent.isStreaming) {
      void message.info(i18n.t('crm.ai_chat.error.busy'));
      return;
    }
    if (disabled) return;
    if (entityChipsRef.current.length + 1 > DEFAULT_ENTITY_CHIP_MAX) {
      void message.info(i18n.t('crm.ai_chat.context_chips.max_reached'));
      return;
    }
    const contextRefs = entityChipsRef.current;
    const attachmentId = pendingFileAttachmentRef.current?.id;
    const send = onExecuteScenario
      ? Promise.resolve(
          onExecuteScenario({
            prompt: commandLabel(command, i18n.locale),
            command,
            contextRefs,
            ...(attachmentId ? { attachmentId } : {}),
          }),
        )
      : agent.send(command, { contextRefs, ...(attachmentId ? { attachmentId } : {}) });
    void send
      .then(() => {
        clearComposerDraft();
        setPendingFileAttachment(null);
      })
      .catch(() => undefined);
  };

  const handleLoadEarlier = (): void => {
    void agent
      .loadEarlier(() => {
        const container = messagesContainerRef.current;
        if (!container) return;
        const containerTop = container.getBoundingClientRect().top;
        const element =
          Array.from(container.querySelectorAll<HTMLElement>('[data-chat-message-id]')).find(
            (item) => item.getBoundingClientRect().bottom >= containerTop,
          ) ?? null;
        prependAnchorRef.current = {
          element,
          offset: (element?.getBoundingClientRect().top ?? containerTop) - containerTop,
          top: container.scrollTop,
          height: container.scrollHeight,
        };
        setHistoryScrollPaused(true);
      })
      .catch(() => undefined);
  };

  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    const anchor = prependAnchorRef.current;
    const lastId = agent.messages.at(-1)?.id;
    if (container && anchor) {
      container.scrollTop = anchor.element?.isConnected
        ? container.scrollTop +
          anchor.element.getBoundingClientRect().top -
          container.getBoundingClientRect().top -
          anchor.offset
        : anchor.top + container.scrollHeight - anchor.height;
      for (const id of pendingApprovalIds) autoScrolledApprovalIdsRef.current.add(id);
      prependAnchorRef.current = null;
    } else if (lastHistoryMessageIdRef.current !== lastId) {
      setHistoryScrollPaused(false);
    }
    lastHistoryMessageIdRef.current = lastId;
  }, [agent.messages, pendingApprovalIds]);

  useEffect(() => {
    const autoScrolledApprovalIds = autoScrolledApprovalIdsRef.current;
    for (const approvalId of autoScrolledApprovalIds) {
      if (!pendingApprovalIds.has(approvalId)) {
        autoScrolledApprovalIds.delete(approvalId);
      }
    }

    if (pendingApprovalIds.size === 0) {
      return;
    }

    const container = messagesContainerRef.current;
    if (!container) {
      return;
    }

    const pendingCards = Array.from(
      container.querySelectorAll<HTMLElement>('[data-testid^="approval-card-"]'),
    ).filter((card) => {
      const testId = card.getAttribute('data-testid');
      const approvalId = testId?.slice('approval-card-'.length);
      return approvalId !== undefined && pendingApprovalIds.has(approvalId);
    });
    const newPendingCards = pendingCards.filter((card) => {
      const testId = card.getAttribute('data-testid');
      const approvalId = testId?.slice('approval-card-'.length);
      return approvalId !== undefined && !autoScrolledApprovalIds.has(approvalId);
    });
    const targetCard = newPendingCards[newPendingCards.length - 1];
    if (!targetCard || typeof targetCard.scrollIntoView !== 'function') {
      return;
    }

    targetCard.scrollIntoView({ block: 'center' });
    for (const card of newPendingCards) {
      const testId = card.getAttribute('data-testid');
      const approvalId = testId?.slice('approval-card-'.length);
      if (approvalId !== undefined) {
        autoScrolledApprovalIds.add(approvalId);
      }
    }
  }, [agent.messages, pendingApprovalIds]);

  const showSlashMenu = draft.startsWith('/');

  /** Текст от последнего отдельного `@` до конца draft-а для name-like частей. */
  const mentionToken = useMemo(() => {
    if (disabled || agent.isStreaming) {
      return null;
    }
    const mentionStart = draft.lastIndexOf('@');
    if (mentionStart < 0 || (mentionStart > 0 && !/\s/u.test(draft[mentionStart - 1] ?? ''))) {
      return null;
    }
    const query = draft.slice(mentionStart + 1);
    const queryParts = query.trim().split(/\s+/u);
    if (queryParts.slice(1).some((part) => !/^[\p{Lu}\p{N}]/u.test(part))) {
      return null;
    }
    return query;
  }, [draft, disabled, agent.isStreaming]);

  // Esc закрывает меню без изменений; новый токен (продолжение ввода после @)
  // снова открывает его.
  const [mentionDismissed, setMentionDismissed] = useState(false);
  useEffect(() => {
    setMentionDismissed(false);
  }, [mentionToken]);

  const showMentionMenu = mentionToken !== null && !mentionDismissed && !showSlashMenu;

  const handleSelectMention = (option: MentionOption): void => {
    setDraft((prev) => {
      const mentionStart = prev.lastIndexOf('@');
      return mentionStart < 0 ? prev : prev.slice(0, mentionStart).trimEnd();
    });
    addEntityChip({ type: option.type, id: option.id, label: option.label });
  };

  return (
    <XProvider>
      <section
        className="crm-chat-surface flex h-full w-full max-w-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white dark:bg-stone-900"
        data-testid="chatpanel-kit"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Messages Stream or Welcome Hero */}
        <div
          ref={messagesContainerRef}
          className="crm-chat-messages-pad relative min-h-0 flex-1 overflow-y-auto crm-chat-messages-scroll bg-white dark:bg-stone-900"
          data-testid="chat-messages-container"
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {agent.hasEarlierMessages && (
            <div className="flex flex-col items-center gap-2 py-2">
              <Button
                onClick={handleLoadEarlier}
                loading={agent.isLoadingEarlier}
                disabled={agent.isStreaming}
              >
                {i18n.t('crm.ai_chat.history.load_earlier')}
              </Button>
              {agent.earlierHistoryError && (
                <span role="status">{i18n.t('crm.ai_chat.history.load_error')}</span>
              )}
            </div>
          )}
          {/* Drag-drop overlay */}
          {isDragging && (
            <div className="absolute inset-0 z-50 flex items-center justify-center rounded-lg border-2 border-dashed border-amber-400 bg-amber-50/80 pointer-events-none">
              <span className="text-sm font-semibold text-amber-700">
                {i18n.t('crm.ai_chat.composer.drop_file_here')}
              </span>
            </div>
          )}
          <div
            className="min-h-full min-w-0 w-full flex flex-col justify-start"
            data-testid="chat-messages-content"
          >
            {agent.messages.length === 0 ? (
              <ChatWelcomeHero
                onSelectPrompt={handleSelectPrompt}
                disabled={disabled || agent.isStreaming}
              />
            ) : (
              <Bubble.List
                items={bubbleItems}
                autoScroll={!agent.isLoadingEarlier && !historyScrollPaused}
                role={chatBubbleRoles}
                classNames={{ root: 'crm-chat-bubble-list' }}
              />
            )}
          </div>
        </div>

        {/* Streaming Gradient Progress Bar */}
        {agent.isStreaming && <div className="crm-streaming-bar w-full shrink-0" />}

        {/* Integrated Bottom Input Area with Suggestion Chips, Voice Input & Slash Commands */}
        {/* Both composer wrappers clip on the X axis only (`overflow-x-clip`,
            not `overflow-hidden`). The slash/mention menus are `bottom-full`
            popovers: they live entirely ABOVE the composer box, so a
            two-axis clip cut them out of the render tree and the tap hit the
            messages container behind them instead of the menu row. `clip`
            keeps the horizontal containment the composer needs (no page-level
            sideways scroll from a wide suggestions row) while letting the
            popovers escape vertically; `hidden` would also have forced the
            unused axis into a scroll container. */}
        <div className="crm-chat-composer-pad w-full min-w-0 shrink-0 overflow-x-clip border-t border-stone-100 dark:border-stone-800 bg-white dark:bg-stone-900">
          <div
            className="group relative w-full max-w-full min-w-0 space-y-2.5 overflow-x-clip"
            onPaste={handlePaste}
          >
            {/* Interactive Slash Command Menu */}
            {showSlashMenu && (
              <SlashCommandMenu
                query={draft}
                onSelect={(cmd) => handleSelectPrompt(cmd.action)}
                onClose={() => setDraft('')}
              />
            )}

            {/* @-mention entity suggestion menu */}
            {showMentionMenu && (
              <MentionMenu
                query={mentionToken}
                onSelect={handleSelectMention}
                onClose={() => setMentionDismissed(true)}
              />
            )}

            {/* Quick Suggestion Chips + Voice Input State. The welcome hero
                already shows these scenarios, so the row stays hidden until
                the conversation has messages. */}
            <div
              hidden={agent.messages.length === 0}
              data-testid="chat-quick-suggestions-scroll"
              className="crm-microscroll block w-full max-w-full min-w-0 touch-pan-x overflow-x-auto overscroll-x-contain pb-0.5"
            >
              <div
                data-testid="chat-quick-suggestions-track"
                className="flex w-max min-w-full items-center justify-between gap-2"
              >
                <div className="flex shrink-0 items-center gap-1.5">
                  <span className="flex shrink-0 items-center gap-1 text-[11px] font-semibold text-stone-400">
                    <Sparkles size={11} className="text-amber-500" />
                  </span>
                  {suggestions.map((sug) => (
                    <button
                      key={sug.id}
                      type="button"
                      disabled={disabled || agent.isStreaming}
                      onClick={() => handleSelectPrompt(sug.action)}
                      className="shrink-0 cursor-pointer rounded-full border border-stone-200/80 dark:border-stone-700/80 bg-stone-50/80 dark:bg-stone-800/90 px-2.5 py-1 text-xs font-medium text-stone-700 dark:text-stone-200 transition hover:border-amber-300 dark:hover:border-amber-600/60 hover:bg-amber-50/60 dark:hover:bg-amber-950/40 hover:text-amber-900 dark:hover:text-amber-200 disabled:opacity-50"
                    >
                      {sug.label}
                    </button>
                  ))}
                </div>

                {/* Voice recognition active badge */}
                {isListening && (
                  <div className="flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-600 border border-red-200 shrink-0">
                    <span className="h-2 w-2 rounded-full bg-red-500 animate-ping" />
                    <span>{i18n.t('crm.ai_chat.voice_input.listening')}</span>
                  </div>
                )}

                {/* Slash commands hint (visible while the composer is focused) */}
                {!showSlashMenu && (
                  <span className="crm-chat-slash-hint shrink-0 items-center gap-1 rounded-full border border-stone-200/70 bg-stone-50 px-2 py-0.5 text-[10px] font-medium text-stone-400 opacity-0 transition-opacity group-focus-within:opacity-100">
                    <Command size={10} className="text-stone-400" aria-hidden="true" />
                    {i18n.t('crm.ai_chat.composer.slash_hint')}
                  </span>
                )}
              </div>
            </div>

            {!agent.isStreaming && agent.canRetryLastRequest && agent.retryLastRequest ? (
              <button
                type="button"
                data-testid="chatpanel-retry"
                onClick={() => void agent.retryLastRequest?.()}
                className="inline-flex items-center gap-1.5 rounded-md border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 px-2.5 py-1 text-xs font-medium text-stone-700 dark:text-stone-300 transition hover:bg-stone-50 dark:hover:bg-stone-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600 mb-1"
              >
                <RotateCcw size={12} aria-hidden="true" />
                {i18n.t('crm.features.features.ai.chat.components.antdesignxchatsurface.povtorit')}
              </button>
            ) : null}

            {/* Hidden file input for paperclip button */}
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              data-testid="chat-file-input"
              onChange={handleFileInputChange}
            />

            {/* PR 5: conversation-owned file waiting for the next message */}
            {pendingFileAttachment !== null && (
              <div className="mb-1 flex" data-testid="chat-pending-file-chip">
                <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-amber-200 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-950/40 px-2.5 py-1 text-xs font-medium text-amber-900 dark:text-amber-200">
                  <Paperclip size={12} aria-hidden="true" className="shrink-0" />
                  <span className="truncate">
                    {i18n.t('crm.ai_chat.file_input.pending.label', {
                      fileName: pendingFileAttachment.fileName,
                    })}
                  </span>
                  <button
                    type="button"
                    aria-label={i18n.t('crm.ai_chat.file_input.pending.remove')}
                    data-testid="chat-pending-file-remove"
                    onClick={() => setPendingFileAttachment(null)}
                    disabled={disabled || agent.isStreaming}
                    className="flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-full text-amber-700 dark:text-amber-300 transition hover:bg-amber-100 dark:hover:bg-amber-900 disabled:opacity-40"
                  >
                    <X size={11} aria-hidden="true" />
                  </button>
                </span>
              </div>
            )}

            {/* Attached entity chips + picker */}
            <EntityChipPicker
              chips={entityChips}
              onAdd={addEntityChip}
              onRemove={removeEntityChip}
              disabled={disabled || agent.isStreaming}
            />

            {/* Sender input */}
            <Sender
              value={draft}
              placeholder={placeholder}
              loading={agent.isStreaming}
              readOnly={disabled}
              components={senderComponents}
              autoSize={{ minRows: 2, maxRows: 6 }}
              classNames={{ root: 'crm-chat-sender' }}
              onCancel={agent.cancelStream}
              onChange={(value) => {
                draftTouchedRef.current = true;
                setDraft(value);
              }}
              onKeyDown={(event) => {
                // Пока открыто @-меню, Enter обрабатывает оно (выбор варианта),
                // а не Sender (отправка сообщения).
                if (showMentionMenu && event.key === 'Enter') {
                  return false;
                }
                // Sender не отправляет при loading, поэтому Enter во время
                // ответа иначе пропадал бы бесследно: текст остаётся, реакции нет.
                if (event.key === 'Enter' && !event.shiftKey && agent.isStreaming) {
                  void message.info(i18n.t('crm.ai_chat.error.busy'));
                  return false;
                }
                return undefined;
              }}
              onSubmit={(value) => {
                const nextValue = value.trim();
                if (!nextValue || disabled || agent.isStreaming) {
                  return;
                }

                const contextRefs = entityChipsRef.current;
                const prevDraft = draft;
                const prevChips = entityChipsRef.current;
                const prevAttachment = pendingFileAttachmentRef.current;
                // The send reads the pending file synchronously while building
                // the turn request, so it must start before the chip state is
                // cleared — otherwise the attachment would never reach the
                // request.
                const sendPromise = sendWithEntityChips(nextValue, contextRefs);
                clearComposerDraft();
                setPendingFileAttachment(null);
                void sendPromise.catch(() => {
                  setDraft(prevDraft);
                  setEntityChips(prevChips);
                  setPendingFileAttachment(prevAttachment);
                  if (
                    canPersistDraft &&
                    conversationId !== undefined &&
                    conversationId !== null &&
                    draftScope
                  ) {
                    writeStoredDraft(draftScope, conversationId, {
                      text: prevDraft,
                      contextRefs: prevChips,
                    });
                  }
                });
              }}
              prefix={
                <span className="flex items-center gap-1">
                  <Tooltip title={i18n.t('crm.ai_chat.composer.attach_file')}>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      aria-label={i18n.t('crm.ai_chat.composer.attach_file')}
                      disabled={disabled || agent.isStreaming || isAttachingFile}
                      className="flex h-7 w-7 items-center justify-center rounded-full transition cursor-pointer shrink-0 text-stone-400 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 hover:text-stone-700 dark:hover:text-stone-200 disabled:opacity-40"
                    >
                      {isAttachingFile ? (
                        <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                      ) : (
                        <Paperclip size={14} />
                      )}
                    </button>
                  </Tooltip>
                  <Tooltip
                    title={
                      isListening
                        ? i18n.t('crm.ai_chat.voice_input.stop')
                        : i18n.t('crm.ai_chat.voice_input.start')
                    }
                  >
                    <button
                      type="button"
                      onClick={handleToggleVoice}
                      aria-label={
                        isListening
                          ? i18n.t('crm.ai_chat.voice_input.stop')
                          : i18n.t('crm.ai_chat.voice_input.start')
                      }
                      disabled={disabled || agent.isStreaming}
                      className={`flex h-7 w-7 items-center justify-center rounded-full transition cursor-pointer shrink-0 ${
                        isListening
                          ? 'crm-voice-recording bg-red-500 text-white'
                          : 'text-stone-400 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 hover:text-stone-700 dark:hover:text-stone-200 disabled:opacity-40'
                      }`}
                    >
                      {isListening ? <MicOff size={14} /> : <Mic size={14} />}
                    </button>
                  </Tooltip>
                </span>
              }
              suffix={(_, { components }) =>
                agent.isStreaming ? (
                  // One control only: the library's loading button is itself a
                  // cancel affordance (the Sender wires onCancel into it), so
                  // rendering it next to our stop button showed two stops.
                  agent.cancelStream ? (
                    <Tooltip
                      title={i18n.t(
                        'crm.features.features.ai.chat.components.antdesignxchatsurface.ostanovit',
                      )}
                      // The stop button replaces the just-clicked Send button and
                      // receives focus; a focus-triggered tooltip would then cover
                      // the composer without any hover. Hover-only keeps it off.
                      trigger="hover"
                    >
                      <button
                        type="button"
                        data-testid="chatpanel-stop"
                        onClick={agent.cancelStream}
                        aria-label={i18n.t(
                          'crm.features.features.ai.chat.components.antdesignxchatsurface.ostanovit',
                        )}
                        className="flex h-8 w-8 items-center justify-center rounded-full border-0 transition cursor-pointer"
                        style={{
                          background: 'var(--g-fill)',
                          color: 'var(--ant-color-text)',
                        }}
                      >
                        <Square size={12} fill="currentColor" aria-hidden="true" />
                      </button>
                    </Tooltip>
                  ) : (
                    <components.LoadingButton
                      aria-label={i18n.t(
                        'crm.features.features.ai.chat.components.antdesignxchatsurface.otvet_formiruetsya',
                      )}
                      disabled
                    />
                  )
                ) : (
                  <components.SendButton
                    aria-label={submitLabel}
                    data-testid="chatpanel-send"
                    disabled={disabled || !draft.trim()}
                  />
                )
              }
            />

            {/* Keyboard hint (visible while the composer is focused) */}
            <div className="flex items-center justify-end px-1 text-[10px] text-stone-500 dark:text-stone-400 opacity-0 transition-opacity group-focus-within:opacity-100">
              <span>{i18n.t('crm.ai_chat.composer.hint_enter')}</span>
            </div>
          </div>
        </div>
      </section>
    </XProvider>
  );
}
